import dayjs, { type Dayjs } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import calendar from 'dayjs/plugin/calendar';
import minMax from 'dayjs/plugin/minMax';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';

import type { UserSettings } from 'src/services/api/users/settings/types';

dayjs.extend(advancedFormat);
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(calendar);
// Required by react-big-calendar's dayjsLocalizer (dayjs.min/max, localeData,
// localized format tokens).
dayjs.extend(minMax);
dayjs.extend(localeData);
dayjs.extend(localizedFormat);

// Curated IANA zones offered when picking a property's touring-hours timezone.
export const TOURING_TIMEZONE_OPTIONS = [
    'America/Toronto',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Vancouver',
    'America/Phoenix',
    'America/Halifax',
    'UTC',
];

// Curated IANA zones offered in timezone pickers, and the fallback list when
// the browser does not expose Intl.supportedValuesOf('timeZone').
export const FALLBACK_TIMEZONES = [
    'America/Los_Angeles',
    'America/Denver',
    'America/Chicago',
    'America/New_York',
    'America/Toronto',
    'Europe/London',
    'Europe/Paris',
    'Asia/Manila',
    'Asia/Tokyo',
    'Australia/Sydney',
];

export enum DateFormat {
    MonthAndDayOfTheMonth = 'MMM Do',
    DayOfTheWeek = 'dddd',
    DayOfTheWeekShort = 'ddd',
}

export enum TimeFormat {
    HoursAndMinutesAndMeridiem = 'h:mm A',
}

export const formatDateTime = (
    str: Dayjs | Date | string | number,
    format: DateFormat | TimeFormat | string,
    fallback = '',
    timezoneOverride?: string | null,
): string => {
    if (!str) {
        return fallback;
    }

    const date = timezoneOverride ? dayjs.utc(str).tz(timezoneOverride) : dayjs(str);
    return date.format(format);
};

export const getUserTimezone = (userSettings?: Pick<UserSettings, 'timezone_override'> | null): string => {
    return userSettings?.timezone_override || dayjs.tz.guess();
};

export const getTimezoneAbbr = (tz: string): string => {
    try {
        const parts = Intl.DateTimeFormat('en', { timeZone: tz, timeZoneName: 'short' }).formatToParts(new Date());
        return parts.find(p => p.type === 'timeZoneName')?.value ?? tz;
    } catch {
        return tz;
    }
};

export const parseDateTimeInUserTimezone = (
    timestamp: string,
    timezoneOverride?: string | null
): Dayjs => {
    if (!timezoneOverride) {
        return dayjs(timestamp);
    }

    return dayjs.utc(timestamp).tz(timezoneOverride);
};

export const getDefaultTourDateTime = (timezoneOverride?: string | null): Dayjs => {
    const now = timezoneOverride ? dayjs().tz(timezoneOverride) : dayjs();
    const defaultNoon = now.hour(12).minute(0).second(0).millisecond(0);
    return defaultNoon.isAfter(now) ? defaultNoon : defaultNoon.add(1, 'day');
};

export const formatDateTimeForApi = (
    date: Dayjs,
    timezoneOverride?: string | null
): string => {
    if (!timezoneOverride) {
        return date.format();
    }

    const wallTime = date.format('YYYY-MM-DDTHH:mm:ss');
    return dayjs.tz(wallTime, timezoneOverride).format();
};

export const formatConversationDateTime = (
    timestamp: string | null | undefined,
    timezoneOverride?: string | null
): string => {
    if (!timestamp) {
        return '';
    }

    const date = timezoneOverride ? dayjs.utc(timestamp).tz(timezoneOverride) : dayjs.utc(timestamp).tz();
    const now = timezoneOverride ? dayjs().tz(timezoneOverride) : dayjs();
    return date.calendar(now, {
        sameDay: 'h:mm A',
        lastDay: 'MMM D',
        lastWeek: 'MMM D',
        sameElse: 'MMM D'
    });
};

export const parseCalendarDateInUserTimezone = (
    timestamp: string,
    timezoneOverride?: string | null
): Dayjs => {
    if (!timezoneOverride) {
        return dayjs(timestamp);
    }

    return dayjs.tz(dayjs.utc(timestamp).tz(timezoneOverride).format('YYYY-MM-DD'), timezoneOverride);
};

export const formatTimelineDate = (timestamp: string, timezoneOverride?: string | null): string => {
    const date = timezoneOverride ? dayjs.utc(timestamp).tz(timezoneOverride) : dayjs.utc(timestamp).local();
    const now = timezoneOverride ? dayjs().tz(timezoneOverride) : dayjs();
    const minutesDiff = now.diff(date, 'minute');

    const dateStartOfDay = date.startOf('day');
    const nowStartOfDay = now.startOf('day');
    const daysDiff = nowStartOfDay.diff(dateStartOfDay, 'day');

    if (minutesDiff < 60 && minutesDiff >= 0) {
        return date.fromNow();
    } else if (daysDiff === 0) {
        return `Today ${date.format('h:mm A')}`;
    } else if (daysDiff === 1) {
        return `Yesterday ${date.format('h:mm A')}`;
    } else {
        return date.format('MM/DD/YY');
    }
};
